import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/poa/jurisdiction/$country")({
  loader: async ({ params }) => {
    const { POA_JURISDICTIONS, getPoaJurisdiction } = await import("@/content/poa-corpus/jurisdictions");
    const j = getPoaJurisdiction(params.country);
    if (!j) throw notFound();
    return { j, siblings: POA_JURISDICTIONS.filter((x) => x.slug !== j.slug).map((x) => ({ slug: x.slug, label: x.nameTh })) };
  },
  component: PoaJurisdictionPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "ไม่พบประเทศปลายทาง" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { j } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `หนังสือมอบอำนาจใช้ที่${j.nameTh} — ลำดับรับรองและข้อควรระวัง`,
        description: j.summaryTh,
        path: `/poa/jurisdiction/${params.country}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/poa/jurisdiction/${params.country}` }],
      }),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/poa/jurisdiction/${params.country}`)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "หนังสือมอบอำนาจข้ามประเทศ", url: `${site}/poa` },
              { name: j.nameTh, url: `${site}/poa/jurisdiction/${params.country}` },
            ]),
          ),
        },
      ],
    };
  },
});

function PoaJurisdictionPage() {
  const { j, siblings } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/poa/faq" className="hover:underline">หนังสือมอบอำนาจ</a> <span>/</span> <span>{j.nameTh}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          หนังสือมอบอำนาจจากไทยเพื่อใช้ที่{j.nameTh} ({j.nameEn})
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">{j.summaryTh}</p>

        <section aria-label="ข้อเท็จจริงหลัก" className="mb-8 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>
              <tr className="border-b">
                <th scope="row" className="w-1/3 py-2 pr-3 text-left align-top font-semibold">ปลายทางเป็นภาคี Apostille</th>
                <td className="py-2 align-top text-muted-foreground">{j.hagueParty ? "เป็นภาคี" : "ไม่เป็นภาคี"}</td>
              </tr>
              <tr className="border-b">
                <th scope="row" className="py-2 pr-3 text-left align-top font-semibold">Apostille กับประเทศไทย</th>
                <td className="py-2 align-top text-muted-foreground">มีผลใช้บังคับวันที่ 28 กุมภาพันธ์ 2570</td>
              </tr>
              <tr className="border-b">
                <th scope="row" className="py-2 pr-3 text-left align-top font-semibold">จำนวนขั้นตอนรับรอง</th>
                <td className="py-2 align-top text-muted-foreground">{j.chainTh.length} ขั้น (ไม่รวมคิวของหน่วยงานปลายทาง)</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section aria-label="ลำดับขั้นตอน" className="mb-8">
          <h2 className="mb-3 text-xl font-bold">ลำดับรับรองทีละขั้น</h2>
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
            {j.chainTh.map((s: string, i: number) => <li key={i}>{s}</li>)}
          </ol>
        </section>

        <section aria-label="กรณีใช้งานที่พบบ่อย" className="mb-8">
          <h2 className="mb-3 text-xl font-bold">กรณีที่ลูกค้าใช้บ่อย</h2>
          <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
            {j.useCasesTh.map((s: string, i: number) => <li key={i}>{s}</li>)}
          </ul>
        </section>

        <ServiceAdviceBlock items={j.adviceTh} locale="th" />
        <ServiceCautionBlock items={j.cautionsTh} locale="th" />

        <section aria-label="แหล่งอ้างอิงของหน้านี้" className="mt-8">
          <h2 className="mb-3 text-xl font-bold">แหล่งอ้างอิงเฉพาะประเทศนี้</h2>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {j.sources.map((s: { label: string; url: string }) => (
              <li key={s.url}>
                <a href={s.url} rel="noopener nofollow" target="_blank" className="underline hover:text-foreground">{s.label}</a>
              </li>
            ))}
          </ul>
        </section>

        <section aria-label="ประเทศอื่น" className="mt-10">
          <h2 className="mb-3 text-xl font-bold">ประเทศปลายทางอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {siblings.map((x: { slug: string; label: string }) => (
              <a key={x.slug} href={`/poa/jurisdiction/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.label}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้เจ้าหน้าที่วางลำดับรับรองให้</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งวัตถุประสงค์และหน่วยงานผู้รับที่{j.nameTh} ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="poa" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}